import { FirstSteps } from './FirstSteps';
import { useNavigate } from 'react-router-dom';
import { useDashboard, useEvents, useTasks } from '../../../platform/api/hooks';
import { asRecord, asList, str } from '../../../platform/api/utils';
import { getSession } from '../../../platform/auth/session';
import { onlyIf, usePermissions } from '../../../platform/auth/permissions';
import { EVENT_STATUS_LABELS, labelOf } from '../../../shared/labels';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Icon } from '../../../components/ui/Icon';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Section } from '../../../components/ui/Section';

const EVENT_STATUS_VARIANTS: Record<string, string> = {
  SCHEDULED: 'info',
  IN_PREPARATION: 'warning',
  HAPPENING: 'success',
  COMPLETED: 'success',
  CANCELLED: 'danger',
  POSTPONED: 'warning',
  ARCHIVED: 'default',
};

const TASK_STATUS_LABELS: Record<string, string> = {
  OVERDUE: 'Atrasada',
  IN_PROGRESS: 'Em andamento',
  TODO: 'A fazer',
};

function parseDate(v: unknown): Date | null {
  if (typeof v !== 'string' && !(v instanceof Date)) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtTime(d: Date | null): string {
  return d ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';
}

function fmtShortDate(v: unknown): string {
  const d = parseDate(v);
  return d
    ? d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }).replace('.', '')
    : '';
}

function fmtCurrency(val: unknown): string {
  let n = 0;
  if (typeof val === 'number') n = val;
  else if (typeof val === 'string') n = Number(val);
  if (Number.isNaN(n)) n = 0;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
}

export function Dashboard() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const goTo = (path: string, permission: string) =>
    can(permission)
      ? () => {
          void navigate(path);
        }
      : undefined;
  const { data: dash, isLoading: dashLoading, isError: dashError } = useDashboard();
  const { data: events, isLoading: eventsLoading } = useEvents();
  const { data: tasks, isLoading: tasksLoading } = useTasks();

  const summary = asRecord(dash);
  const peopleSummary = asRecord(summary.peopleSummary);
  const hasFinance = summary.financeSummary != null;
  const hasPeople = summary.peopleSummary != null;
  const hasObligations = summary.obligationsSummary != null;
  const hasInventory = summary.inventoryAlerts != null;
  const financeSummary = asRecord(summary.financeSummary);
  const peopleByStatus = asRecord(peopleSummary.byStatus);

  const upcomingFromDash = asList(summary.upcomingEvents);
  const upcomingFromApi = asList(events).slice(0, 5);
  const upcoming = upcomingFromDash.length > 0 ? upcomingFromDash : upcomingFromApi;

  const pendingFromDash = asList(summary.pendingTasks);
  const pending = (pendingFromDash.length > 0 ? pendingFromDash : asList(tasks))
    .filter((t) => {
      const r = asRecord(t);
      return r.status === 'OVERDUE' || r.status === 'TODO' || r.status === 'IN_PROGRESS';
    })
    .slice(0, 5);

  const lowStock = asList(summary.inventoryAlerts).length;
  const activeMembers = peopleByStatus.ACTIVE ?? peopleSummary.total;
  const monthBalance = financeSummary.balance;
  const houseName = getSession()?.houseName ?? 'Sua casa';

  const loading = dashLoading || eventsLoading || tasksLoading;

  if (dashError) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1320 }}>
        <PageHeader title="A casa, vista com clareza." subtitle={houseName} />
        <Section title="Não foi possível carregar">
          <p style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>
            Não foi possível carregar os dados do painel. Tente novamente em instantes.
          </p>
        </Section>
      </div>
    );
  }

  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1320 }}>
      <PageHeader
        title="A casa, vista com clareza."
        subtitle={`${houseName} · ${new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}`}
        action={onlyIf(can('tasks.create'), {
          label: 'Nova tarefa',
          onClick: () => {
            void navigate('/tarefas?nova=1');
          },
        })}
      />

      <FirstSteps />

      {loading ? (
        <SkeletonCards />
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
              gap: '1rem',
              marginBottom: '1.5rem',
            }}
          >
            {hasPeople && (
              <Card
                title="Pessoas ativas"
                value={str(activeMembers, '—')}
                subtitle="Cadastros ativos da casa"
                icon="users"
                variant="highlight"
                onClick={goTo('/pessoas', 'community.people.read')}
              />
            )}
            <Card
              title="Compromissos"
              value={str(upcoming.length, '—')}
              subtitle="Próximos no calendário"
              icon="calendar"
              onClick={goTo('/calendario', 'calendar.events.read')}
            />
            <Card
              title="Tarefas pendentes"
              value={str(pendingFromDash.length || pending.length, '—')}
              subtitle="Para acompanhar com a equipe"
              icon="list"
              onClick={goTo('/tarefas', 'tasks.read')}
            />
            {hasInventory && (
              <Card
                title="Itens para repor"
                value={str(lowStock, '0')}
                subtitle={lowStock > 0 ? 'Abaixo do mínimo cadastrado' : 'Nenhum alerta'}
                icon="box"
                variant={lowStock > 0 ? 'danger' : 'default'}
                onClick={goTo('/estoque', 'inventory.items.read')}
              />
            )}
            {hasFinance && (
              <Card
                title="Saldo registrado"
                value={fmtCurrency(monthBalance)}
                subtitle="Entradas menos saídas"
                icon="coins"
                variant={Number(monthBalance) < 0 ? 'danger' : 'default'}
                onClick={goTo('/financeiro', 'finance.entries.read')}
              />
            )}
            {hasObligations && (
              <Card
                title="Obrigações em aberto"
                value={str(asRecord(summary.obligationsSummary).total, '—')}
                subtitle="Compromissos administrativos"
                icon="candle"
                onClick={goTo('/obrigacoes', 'obligations.read')}
              />
            )}
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
              gap: '1.5rem',
            }}
          >
            <Section
              title="Os próximos encontros."
              action={
                <button
                  type="button"
                  className="text-button"
                  onClick={() => {
                    void navigate('/calendario');
                  }}
                >
                  Ver agenda <Icon name="arrow" size={13} />
                </button>
              }
            >
              {upcoming.length === 0 ? (
                <EmptyState title="Nenhum compromisso por perto." copy="O calendário está livre." />
              ) : (
                upcoming.map((ev, i) => {
                  const e = asRecord(ev);
                  const st = str(e.status);
                  const d = parseDate(e.startsAt);
                  return (
                    <div className="event-row" key={str(e.id, String(i))}>
                      <div className="event-date">
                        <strong>{d ? d.getDate() : '—'}</strong>
                        <span>
                          {d
                            ? d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '')
                            : ''}
                        </span>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <h3>{str(e.title)}</h3>
                        <p>{fmtTime(d)}</p>
                      </div>
                      <Badge
                        label={labelOf(EVENT_STATUS_LABELS, st)}
                        variant={EVENT_STATUS_VARIANTS[st] ?? 'default'}
                      />
                    </div>
                  );
                })
              )}
            </Section>

            <Section
              title="O que pede atenção."
              action={
                <button
                  type="button"
                  className="text-button"
                  onClick={() => {
                    void navigate('/tarefas');
                  }}
                >
                  Ver tarefas <Icon name="arrow" size={13} />
                </button>
              }
            >
              {pending.length === 0 ? (
                <EmptyState title="Tudo em dia por aqui." copy="Nenhuma tarefa pendente." />
              ) : (
                pending.map((t, i) => {
                  const task = asRecord(t);
                  const status = str(task.status);
                  return (
                    <button
                      type="button"
                      className="event-row"
                      key={str(task.id, String(i))}
                      onClick={() => {
                        if (task.id) void navigate(`/tarefas/${str(task.id)}`);
                      }}
                      style={{
                        width: '100%',
                        background: 'transparent',
                        border: 0,
                        borderTop: i === 0 ? 0 : '1px solid var(--line-soft)',
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: 'inherit',
                      }}
                    >
                      <span
                        className="icon-box"
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 7,
                          background: status === 'OVERDUE' ? 'var(--terra-soft)' : 'var(--soft)',
                          color: status === 'OVERDUE' ? 'var(--terra)' : 'var(--green)',
                        }}
                      >
                        <Icon name="list" size={14} />
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <h3>{str(task.title)}</h3>
                        <p>{TASK_STATUS_LABELS[status] ?? 'A fazer'}</p>
                      </div>
                      <span
                        style={{
                          marginLeft: 'auto',
                          fontSize: '0.68rem',
                          color: 'var(--muted)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {fmtShortDate(task.dueDate)}
                      </span>
                    </button>
                  );
                })
              )}
            </Section>
          </div>
        </>
      )}
    </div>
  );
}

function SkeletonCards() {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
        gap: '1rem',
      }}
    >
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 124 }} />
      ))}
    </div>
  );
}
